// src/pages/common-components/AIChat/ClientAIChat.tsx
//
// Floating shopping-assistant chat widget, mounted once globally in App.tsx.
// Talks to the `clientChat` Cloud Function (Gemini-powered) — see
// new/functions/functions/functions/src/ai.ts. No login required.
//
// Guardrails (never reveal passwords/other customers'/internal financial
// data, only answer the specific verified order if looked up) are enforced
// server-side in the Cloud Function — this component is just the UI.

import React, { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../Firebase/Config';

const functions = getFunctions(app, 'asia-south1');

interface ChatTurn {
  role: 'user' | 'model';
  text: string;
}

interface ClientChatResponse {
  reply: string;
}

// Gemini replies sometimes include lightweight markdown (**bold**, "- " bullet
// lists) even when told not to — render it instead of showing literal `**`.
const parseInline = (line: string, keyPrefix: string): React.ReactNode[] =>
  line
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part.length > 0)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
      )
    );

const renderFormattedText = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listBuffer: React.ReactNode[] = [];

  const flushList = (key: string) => {
    if (listBuffer.length > 0) {
      nodes.push(
        <ul key={key} style={{ margin: '4px 0', paddingLeft: 20 }}>
          {listBuffer}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, idx) => {
    const bulletMatch = /^\s*[-*]\s+(.*)/.exec(line);
    if (bulletMatch) {
      listBuffer.push(<li key={`li-${idx}`}>{parseInline(bulletMatch[1], `li-${idx}`)}</li>);
      return;
    }
    flushList(`ul-${idx}`);
    nodes.push(
      <React.Fragment key={`line-${idx}`}>
        {parseInline(line, `line-${idx}`)}
        {idx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
  flushList('ul-end');

  return nodes;
};

const clientChatCallable = httpsCallable<
  { message: string; history: ChatTurn[]; orderNumber?: string; email?: string },
  ClientChatResponse
>(functions, 'clientChat');

const ClientAIChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setError('');
    setInput('');
    const nextMessages: ChatTurn[] = [...messages, { role: 'user', text }];
    setMessages(nextMessages);
    setSending(true);

    try {
      const result = await clientChatCallable({ message: text, history: messages });
      setMessages([...nextMessages, { role: 'model', text: result.data.reply }]);
    } catch (err: any) {
      setError(err?.message || 'Sorry, something went wrong. Please try again.');
      setMessages(nextMessages);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button
        type="button"
        className="ai-chat-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat assistant'}
      >
        <i className={`zmdi ${open ? 'zmdi-close' : 'zmdi-comment-text'}`} />
      </button>

      {open && (
        <div className="ai-chat-panel">
          <div className="ai-chat-header">
            <div>
              <div className="ai-chat-title">Shopping Assistant</div>
              <div className="ai-chat-subtitle">Ask about orders, shipping, branches &amp; more</div>
            </div>
            <button type="button" className="ai-chat-close" onClick={() => setOpen(false)} aria-label="Close">
              <i className="zmdi zmdi-close" />
            </button>
          </div>

          <div className="ai-chat-messages" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                <i className="zmdi zmdi-comment-outline" />
                <p>Hi! Ask me about our products and prices, how to order, track a delivery, or find a branch.</p>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`ai-chat-bubble-row ${m.role === 'user' ? 'user' : 'bot'}`}>
                <div className="ai-chat-bubble">{renderFormattedText(m.text)}</div>
              </div>
            ))}

            {sending && <div className="ai-chat-typing">Thinking…</div>}
            {error && <div className="ai-chat-error">{error}</div>}
          </div>

          <div className="ai-chat-input-row">
            <textarea
              className="ai-chat-input"
              placeholder="Type your question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              rows={1}
            />
            <button
              type="button"
              className="ai-chat-send"
              onClick={handleSend}
              disabled={sending || !input.trim()}
              aria-label="Send"
            >
              <i className="zmdi zmdi-mail-send" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .ai-chat-fab {
          position: fixed;
          bottom: 90px;
          right: 24px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #717fe0;
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(113,127,224,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 9998;
          font-size: 24px;
          transition: background 0.2s;
        }
        .ai-chat-fab:hover { background: #5c68c9; }

        .ai-chat-panel {
          position: fixed;
          bottom: 158px;
          right: 24px;
          width: 340px;
          max-width: calc(100vw - 32px);
          height: 480px;
          max-height: calc(100vh - 140px);
          background: #fff;
          border-radius: 14px;
          border: 1px solid #e6e6e6;
          box-shadow: 0 15px 50px rgba(0,0,0,0.2);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 9998;
          font-family: 'Poppins-Regular', sans-serif;
        }

        .ai-chat-header {
          background: #717fe0;
          color: #fff;
          padding: 14px 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .ai-chat-title { font-family: 'Poppins-Medium', sans-serif; font-size: 14px; }
        .ai-chat-subtitle { font-size: 11px; opacity: 0.85; margin-top: 3px; }
        .ai-chat-close {
          background: none; border: none; color: #fff; cursor: pointer;
          font-size: 16px; line-height: 1; padding: 2px;
        }

        .ai-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 14px 16px;
          background: #faf9fd;
        }
        .ai-chat-empty {
          text-align: center;
          color: #999;
          margin-top: 30px;
          padding: 0 12px;
        }
        .ai-chat-empty i { font-size: 32px; opacity: 0.4; }
        .ai-chat-empty p { font-size: 13px; margin-top: 8px; line-height: 1.6; }

        .ai-chat-bubble-row { display: flex; margin-bottom: 10px; }
        .ai-chat-bubble-row.user { justify-content: flex-end; }
        .ai-chat-bubble-row.bot { justify-content: flex-start; }
        .ai-chat-bubble {
          max-width: 85%;
          padding: 9px 12px;
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.6;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .ai-chat-bubble-row.user .ai-chat-bubble { background: #717fe0; color: #fff; }
        .ai-chat-bubble-row.bot .ai-chat-bubble { background: #fff; color: #333; border: 1px solid #e6e6e6; }

        .ai-chat-typing { font-size: 12px; color: #999; margin-top: 4px; }
        .ai-chat-error { font-size: 12px; color: #f74877; margin-top: 6px; }

        .ai-chat-input-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 10px 12px;
          border-top: 1px solid #e6e6e6;
          flex-shrink: 0;
        }
        .ai-chat-input {
          flex: 1;
          resize: none;
          border: 1px solid #e6e6e6;
          border-radius: 10px;
          padding: 8px 10px;
          font-size: 13px;
          font-family: 'Poppins-Regular', sans-serif;
          max-height: 80px;
          outline: none;
        }
        .ai-chat-input:focus { border-color: #717fe0; }
        .ai-chat-send {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #717fe0;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 16px;
        }
        .ai-chat-send:disabled { background: #c6cbef; cursor: not-allowed; }

        @media (max-width: 480px) {
          .ai-chat-panel { right: 16px; bottom: 148px; width: calc(100vw - 32px); }
          .ai-chat-fab { right: 16px; bottom: 80px; }
        }
      `}</style>
    </>
  );
};

export default ClientAIChat;
